import { useEffect, useState, useRef } from "react";
import { Button, Divider, Drawer, Typography, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import HeaderComponent from "../header";
import SidebarComponent from "../sidebar";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { getUiIncomeLimit } from "../../store/thunk/authThunk";
import { setFaqDialog, setPreviewDialog } from "../../store/slices/flowFormSlice";
import FAQs from "../../pages/faq";
import PreviewPDF from "../../pages/previewPdf";

import { getAllPromptsData, getSingleForm, replenish } from "../../store/thunk/formThunk";
import { anonymousUser, logErrorFunction, logFunction } from "../../helper/service";
import SideLogo from "../../assets/images/logo-white.png";
import { getReady } from "../../store/thunk/userThunk";
import { Error } from "@mui/icons-material";

const drawerWidth = 400;
const LayoutComponent = ({ children }: any) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const valueReadyRef: any = useRef();
  const hidden = useMediaQuery("(min-width:900px)");
  const { authConfig } = useAppSelector((state: any) => state.authReducer);
  const { progressData } = useAppSelector((state: any) => state.documentReducer);
  const { isCaseTypeSubmitted } = useAppSelector((state: any) => state.notificationReducer);
  const uiFlowJsonId = localStorage.getItem("uiFlowJsonId")
    ? localStorage.getItem("uiFlowJsonId")
    : "";

  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [sectionList, setSectionList] = useState<any[]>([]);
  const [, setLocked] = useState<any>(false);
  const [isInIframe, setIsInIframe] = useState(false);

  const isFaqDialog = useAppSelector((state: any) => state.formReducer.faqDialog);
  const previewDialog = useAppSelector((state: any) => state.formReducer.previewDialog);
  const { getSignleFormData } = useAppSelector((state: any) => state?.formReducer);
  const [openFAQ, setIsOpenFAQ] = useState<any>(false);
  const [openPreview, setIsOpenPreview] = useState<any>(false);
  const [route, setRoute] = useState<any>(false);
  const [sidebarLogoURL, setSidebarLogoURL] = useState("");
  const { sidebarLogo } = useAppSelector((state: any) => state.billingReducer);
  const [readyInterval, setReadyInterval] = useState<any>(null);

  const isAdmin = localStorage.getItem("userType")?.toLocaleLowerCase() === "admin";

  const localData: any = localStorage.getItem("bootstrapData");
  const bootstrapData: any = JSON.parse(localData);
  const { caseTypes } = bootstrapData || {};

  const localIsBootstrapCalled: any = localStorage.getItem("isBootstrapCalled");
  const isBootstrapCalled: any = JSON.parse(localIsBootstrapCalled);

  const lockedApp = localStorage.getItem("lockedApp");

  const isCaseType = localStorage.getItem("isCaseType") ? localStorage.getItem("isCaseType") : null;
  const role: any = localStorage.getItem("role");
  const isIntelligenceEnabled = bootstrapData?.features?.find(
    (f: any) => f?.name === "IntelligenceEnabled"
  )?.value;
  useEffect(() => {
    if (isIntelligenceEnabled && role === "lawyer") {
      dispatch(getAllPromptsData());
    }
  }, [isIntelligenceEnabled, role]);

  useEffect(() => {
    if (window.top !== window.self) {
      setIsInIframe(true);
    }
  }, [location]);

  useEffect(() => {
    if (sidebarLogo !== null) {
      setSidebarLogoURL(sidebarLogo);
    }
  }, [sidebarLogo]);

  useEffect(() => {
    dispatch(setFaqDialog(false));
    dispatch(setPreviewDialog(false));
  }, []);

  useEffect(() => {
    if (bootstrapData?.refreshReady && isBootstrapCalled) {
      dispatch(getUiIncomeLimit());
    }
  }, [bootstrapData?.refreshReady]);

  useEffect(() => {
    valueReadyRef.current = readyInterval;
  }, [readyInterval]);

  const onClearReadyInterval = () => {
    setReadyInterval(null);
    clearInterval(valueReadyRef.current);
  };

  // Continue call to check ready status
  useEffect(() => {
    if (isBootstrapCalled) {
      if (bootstrapData?.refreshReady === false && bootstrapData?.refreshReady !== null) {
        const readyInterval = setInterval(
          () =>
            dispatch(getReady()).then((res: any) => {
              if (res?.payload?.data === true) {
                bootstrapData.refreshReady = true;
                localStorage.setItem("bootstrapData", JSON.stringify(bootstrapData));
                onClearReadyInterval();
              }
            }),
          2000
        );
        setReadyInterval(readyInterval);
      } else {
        onClearReadyInterval();
      }
    }
  }, []);

  // Stop Status API after 30 seconds if status=paid/unpaid and stop api call of ready
  useEffect(() => {
    setTimeout(() => {
      onClearReadyInterval();
    }, 30000);
  }, []);

  useEffect(() => {
    if (uiFlowJsonId && isBootstrapCalled && isCaseType === "true") {
      dispatch(getSingleForm({ uiFlowJsonId }));
    }
  }, [localStorage.getItem("uiFlowJsonId"), isCaseTypeSubmitted, isCaseType]);

  useEffect(() => {
    const unSavedFormValues = sessionStorage.getItem("unSavedFormValues");

    if (unSavedFormValues) {
      try {
        const parsedValues = JSON.parse(unSavedFormValues);

        if (parsedValues?.ui_form_lock_state !== undefined) {
          setLocked(parsedValues.ui_form_lock_state);
        } else {
          setLocked(false);
        }
      } catch (error) {
        logErrorFunction("Error parsing session storage data:" + error);
        setLocked(false);
      }
    } else {
      setLocked(false);
    }
  }, []);

  useEffect(() => {
    const isIframeUser = (function () {
      try {
        return window.self !== window.top;
      } catch {
        return true; // In case of cross-origin restrictions
      }
    })();

    if (location.pathname && sectionList.length) {
      const dynamicPathName = decodeURI(location.pathname);
      const splittedArray = dynamicPathName?.split("/");
      const [, , mainId] = splittedArray;

      if (mainId) {
        const selectedSection = sectionList?.find((section) => section?.data?.nodeTitle === mainId);

        if (!selectedSection?.id) {
          navigate("/");
        }
      } else {
        if (isIframeUser) {
          const isEligibilityFilled = progressData?.find(
            (section: any) => section?.sectionKey === "General_Eligibility"
          );
          if (isEligibilityFilled && isEligibilityFilled?.percentComplete === 0) {
            navigate("/home");
          }
          logFunction("Iframe user detected");
        } else {
          if (isCaseType && isCaseType !== "false") {
            const isEligibilityFilled = progressData?.find(
              (section: any) => section?.sectionKey === "General_Eligibility"
            );
            if (isEligibilityFilled && isEligibilityFilled?.percentComplete === 0) {
              navigate("/eligibility-check/Eligibility%20Quiz");
            }
          }
        }
      }
    }
  }, [location.pathname, sectionList, progressData]);

  const convertIntoUiJson = (jsonData: any) => {
    try {
      const orderCounts: any = [];

      return (
        !!jsonData &&
        jsonData.length > 0 &&
        jsonData.map((val: any) => {
          const childList = jsonData
            .filter((row: any) => val.id === row.parentNode)
            .map((row: any) => row.id);
          const order = orderCounts.filter((row: any) => val.parentNode === row);
          orderCounts.push(val.parentNode);

          return {
            id: val.id,
            // nodeTitle: val?.data?.nodeTitle ? val?.data?.nodeTitle : "",
            parentNode: val?.parentNode,
            order: (!!order?.length ? order?.length : 0) + 1,
            children: childList,
            // data: val?.data,
          };
        })
      );
    } catch {
      alert("json is not correct.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const getValue =
        getSignleFormData && getSignleFormData?.flowJson
          ? JSON.parse(getSignleFormData?.flowJson)
          : "";
      if (!!getValue?.nodes && getValue?.nodes?.length > 0) {
        const getJsonData: any = await convertIntoUiJson(getValue?.nodes);

        const mergedObj = getValue?.nodes?.flatMap((flowJsonData: any) =>
          getJsonData
            ?.filter((getJsonDataItem: any) => flowJsonData.id === getJsonDataItem.id)
            .map((matchingItem: any) => ({ ...matchingItem, ...flowJsonData }))
        );

        const sectionData = mergedObj.filter((nodesData: any) => {
          if (
            nodesData?.data?.nodeType &&
            (nodesData?.data?.nodeType?.toLowerCase() === "section" ||
              nodesData?.data?.nodeType?.toLowerCase() === "text") &&
            !!nodesData?.data?.nodeTitle &&
            nodesData?.id !== "Evidence_Engine" &&
            nodesData?.id !== "Special_Forms" &&
            nodesData?.id !== "Pay_and_Download" &&
            nodesData?.id !== "Printsign"
          ) {
            return nodesData;
          }
        });

        if (!!sectionData?.length && sectionData?.length > 0) {
          setSectionList(sectionData);
        }
      }
    };

    fetchData();
  }, [getSignleFormData]);

  useEffect(() => {
    if (bootstrapData?.refreshReady === true && isBootstrapCalled) {
      dispatch(replenish());
    }
  }, [authConfig, bootstrapData?.refreshReady]);

  useEffect(() => {
    if (bootstrapData && (bootstrapData?.isDisabled || bootstrapData?.isDeleted)) {
      navigate("/disable-user");
    }
  }, [bootstrapData]);

  useEffect(() => {
    setRoute(location?.pathname || "");
  }, [location]);

  useEffect(() => {
    setIsOpenFAQ(isFaqDialog);
  }, [isFaqDialog]);

  useEffect(() => {
    setIsOpenPreview(previewDialog);
  }, [previewDialog]);

  const toggleFaqDrawer = () => {
    dispatch(setFaqDialog(!openFAQ));
    dispatch(setPreviewDialog(false));
  };
  const togglePreviewDrawer = () => {
    dispatch(setFaqDialog(false));
    dispatch(setPreviewDialog(!openPreview));
  };

  const getDrawerClassName = () => {
    if (route === "/upload-document") return "";
    if (!isAdmin) return `faqDrawer ${anonymousUser() ? "AnonymousUserBox" : "UserBox"}`;
    return "faqDrawer";
  };

  return (
    <div>
      <div className="flex md:hidden overflow-hidden justify-between items-center p-2.5 bg-[#111827] h-[77px]">
        {isInIframe ? (
          sidebarLogoURL && <img alt="Law Firm Logo" className="sideLogo" src={sidebarLogoURL} />
        ) : (
          <img alt="Logo" className="sideLogo" src={SideLogo} />
        )}

        <button
          aria-controls="sidebar-multi-level-sidebar"
          className="inline-flex items-center p-2 text-sm text-white hover:text-black rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 hemburger-menu"
          data-drawer-target="sidebar-multi-level-sidebar"
          data-drawer-toggle="sidebar-multi-level-sidebar"
          onClick={() => setIsSidebarVisible(!isSidebarVisible)}
          type="button"
        >
          <svg
            aria-hidden="true"
            className="w-6 h-6"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
              fillRule="evenodd"
            />
          </svg>
        </button>
      </div>
      <div>
        <div
          className={`main ml-auto fixed w-full overflow-hidden ${
            isSidebarVisible ? "sideBarHide" : "sideBarShow"
          }`}
        >
          <SidebarComponent setIsSidebarVisible={setIsSidebarVisible} />
          <div
            className={`main mainContentSection h-screen ${
              isSidebarVisible ? "sideBarHide" : "sideBarShow"
            } ${sessionStorage.getItem("Usapath-Emulate-UserId") ? "has-banner" : ""} ${
              caseTypes?.length > 1 ? "has-multi-caseTypes" : ""
            } ${lockedApp === "true" ? "locked-forms" : ""}`}
          >
            {isSidebarVisible && (
              <div
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                style={{
                  background: "rgba(0, 0, 0, 0.5)",
                  zIndex: 1000,
                  position: "fixed",
                  width: "100%",
                  height: "100%",
                }}
              />
            )}
            <div className={(openFAQ || openPreview) && hidden ? "openFAQWdith" : "closeFAQWdith"}>
              {route !== "/upload-document" && <HeaderComponent />}
              <div className="h-full mainInnerContent">{children}</div>
            </div>

            {openFAQ && (
              <Drawer
                anchor="right"
                className={getDrawerClassName()}
                open={openFAQ}
                sx={{
                  maxWidth: drawerWidth,
                  width: "100%",
                  flexShrink: 0,
                  "& .MuiDrawer-paper": {
                    maxWidth: drawerWidth,
                    width: "100%",
                    ...(hidden && {
                      top: (() => {
                        const isEmulated = sessionStorage.getItem("Usapath-Emulate-UserId");
                        const isLocked = lockedApp === "true";

                        if (isEmulated && isLocked) return "243px";
                        if (isEmulated || isLocked) return "186px";
                        return "126px";
                      })(),
                    }),
                    ...(hidden && {
                      height: (() => {
                        const isEmulated = sessionStorage.getItem("Usapath-Emulate-UserId");
                        const isLocked = lockedApp === "true";

                        if (isEmulated && isLocked) return "calc(100vh - 243px)";
                        if (isEmulated || isLocked) return "calc(100vh - 187px)";
                        if (anonymousUser()) return "calc(100vh - 40px)";
                        return "calc(100vh - 126px)";
                      })(),
                    }),
                  },
                }}
                variant={hidden ? "persistent" : "temporary"}
              >
                <FAQs toggleFaqDrawer={toggleFaqDrawer} />
              </Drawer>
            )}
            {openPreview && (
              <Drawer
                anchor="right"
                className={(() => {
                  if (!isAdmin) {
                    return anonymousUser() ? "AnonymousUserBox" : "UserBox";
                  }
                  return "";
                })()}
                open={openPreview}
                sx={{
                  maxWidth: drawerWidth,
                  width: "100%",
                  flexShrink: 0,
                  "& .MuiDrawer-paper": {
                    maxWidth: drawerWidth,
                    width: "100%",
                    ...(hidden && {
                      top: (() => {
                        const isEmulated = sessionStorage.getItem("Usapath-Emulate-UserId");
                        const isLocked = lockedApp === "true";

                        if (isEmulated && isLocked) return "243px";
                        if (isEmulated || isLocked) return "186px";
                        return "126px";
                      })(),
                    }),
                    ...(hidden && {
                      height: (() => {
                        const isEmulated = sessionStorage.getItem("Usapath-Emulate-UserId");
                        const isLocked = lockedApp === "true";

                        if (isEmulated && isLocked) return "calc(100vh - 243px)";
                        if (isEmulated || isLocked) return "calc(100vh - 187px)";
                        if (anonymousUser()) return "calc(100vh - 40px)";
                        return "calc(100vh - 100px)";
                      })(),
                    }),
                  },
                }}
                variant={hidden ? "persistent" : "temporary"}
              >
                <PreviewPDF hidden={hidden} togglePreviewDrawer={togglePreviewDrawer} />
              </Drawer>
            )}

            {/* <FooterComponent /> */}
          </div>
        </div>
      </div>
      <div className="errorScreen" id="errorScreen">
        <div>
          <Typography variant="h6">
            <Error sx={{ color: "#d32f2f" }} /> Something went wrong
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 16 }} variant="inherit">
            Please refresh the page and try again.
          </Typography>
          <small style={{ color: "#707070" }}>
            <b>Request ID: </b>
            <span />
          </small>
          <Button
            onClick={() => window.location.reload()}
            size="small"
            sx={{ mt: 2, width: "100%" }}
            variant="contained"
          >
            Refresh the page
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LayoutComponent;
